import { getDisplayQuantity, getEstimateNumberValueBasedOnSettings, getMeasurementDetails } from "@Actions/estimation";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { useRef } from "react";
import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { RootState } from "@Reducers/index";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema";

type LoadedDisplayUnit = NonNullable<EstimateData["linearDisplayUnit"]> & {
  factor: number;
  selectedSymbol: { name: string };
  type: { name: string };
};
type LoadedEstimateData = EstimateData &
  Record<
    "linearDisplayUnit" | "areaDisplayUnit" | "volumeDisplayUnit" | "countDisplayUnit" | "angleDisplayUnit",
    LoadedDisplayUnit
  >;

const DeriveMeasurements = ({ dataSource, derivedMeasurementsSelectingEvents, selectedMeasurements }: any) => {
  const { t } = useTranslation();
  const gridRef: any = useRef<any>(null);
  const estimateData = useSelector((state: RootState) => state.estimateData);

  const templateHelper = (name: any) => getMeasurementDetails(name);

  const handleRowSelection = () => {
    const selectedRecords = gridRef.current.slickGrid
      .getSelectedRows()
      .map((data: any) => gridRef.current.slickGrid.getDataItem(data))
      .filter((data: any) => data);
    derivedMeasurementsSelectingEvents(selectedRecords);
  };

  const derivedMeasurementsDataBound = () => {
    if (selectedMeasurements.length && gridRef.current) {
      const rowIndices: any = [];
      dataSource.forEach((measurement: any, index: number) => {
        if (selectedMeasurements.find((record: any) => record.id == measurement.id)) {
          rowIndices.push(index);
        }
      });
      gridRef.current.dataView.onRowsChanged.subscribe(function () {
        gridRef.current.slickGrid.setSelectedRows(rowIndices);
      });
    }
  };

  const deriveMeasurementsNameTemplate = (data: any, value: any, a: any, b: any, params: any) =>
    templateHelper(params._name).name;

  const deriveMeasurementsQuantityTemplate = (data: any, val: any, a: any, b: any, params: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    let factor = 1;
    const { type } = templateHelper(params._name);
    const { linearDisplayUnit, areaDisplayUnit, volumeDisplayUnit, countDisplayUnit, angleDisplayUnit } =
      estimateData as LoadedEstimateData;

    if (linearDisplayUnit.type.name === type) factor = linearDisplayUnit.factor;
    if (areaDisplayUnit.type.name === type) factor = areaDisplayUnit.factor;
    if (volumeDisplayUnit.type.name === type) factor = volumeDisplayUnit.factor;
    if (countDisplayUnit.type.name === type) factor = countDisplayUnit.factor;
    if (angleDisplayUnit.type.name === type) factor = angleDisplayUnit.factor;

    const displayQuantity = getDisplayQuantity({
      quantity: params._measurementValue * params._unitMultiplier,
      factor,
    });
    const value = displayQuantity ? displayQuantity.quantity! : 0;
    root.render(<div>{getEstimateNumberValueBasedOnSettings(value, estimateData, "measurement")}</div>);
    return container;
  };

  const deriveMeasurementsUnitTemplate = (data: any, value: any, a: any, b: any, params: any) => {
    let selectedSymbol: any = {};
    const { type } = templateHelper(params._name);
    const { linearDisplayUnit, areaDisplayUnit, volumeDisplayUnit, countDisplayUnit, angleDisplayUnit } =
      estimateData as LoadedEstimateData;

    if (linearDisplayUnit.type.name === type) selectedSymbol = linearDisplayUnit.selectedSymbol;
    if (areaDisplayUnit.type.name === type) selectedSymbol = areaDisplayUnit.selectedSymbol;
    if (volumeDisplayUnit.type.name === type) selectedSymbol = volumeDisplayUnit.selectedSymbol;
    if (countDisplayUnit.type.name === type) selectedSymbol = countDisplayUnit.selectedSymbol;
    if (angleDisplayUnit.type.name === type) selectedSymbol = angleDisplayUnit.selectedSymbol;

    return selectedSymbol.name;
  };

  const deriveMeasurementColumns = [
    {
      field: "_name",
      name: t("Name"),
      formatter: deriveMeasurementsNameTemplate,
      textAlign: "Left",
    },
    {
      field: "_measurementValue",
      name: t("Measurement"),
      formatter: deriveMeasurementsQuantityTemplate,
      textAlign: "Left",
    },
    {
      field: "units",
      name: t("Units"),
      formatter: deriveMeasurementsUnitTemplate,
      textAlign: "Left",
    },
  ];

  return (
    <div className="overflow-auto h-full w-full">
      <SlickGrid1
        ref={gridRef}
        showPageCount={false}
        columnDefinitions={deriveMeasurementColumns as any}
        rowHeight={50}
        dataset={dataSource}
        onSelectedRowIdsChanged={handleRowSelection}
        onReactGridCreated={derivedMeasurementsDataBound}
        hideNoDataIllustration
      />
    </div>
  );
};

export default DeriveMeasurements;
